import React, { useState, useEffect } from 'react';
import api from '../services/api';
import { useAuth } from '../contexts/AuthContext';
import './Testimonials.css';

const Testimonials = () => {
  const [testimonials, setTestimonials] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const { getToken } = useAuth();
  const token = getToken();

  useEffect(() => {
    fetchTestimonials();
  }, []);

  const fetchTestimonials = async () => {
    try {
      setLoading(true);
      const response = await api.get('/testimonials');
      
      if (response.data.success) {
        setTestimonials(response.data.data);
      } else {
        setError(response.data.message || 'Failed to fetch testimonials');
      }
    } catch (err) {
      setError(err.response?.data?.message || err.message || 'An error occurred while fetching testimonials');
    } finally {
      setLoading(false);
    }
  };

  const handleApproveTestimonial = async (testimonialId, isApproved) => {
    try {
      const response = await api.put(`/testimonials/${testimonialId}/approve`, { isApproved });

      if (response.data.success) {
        // Update the testimonial in the local state
        setTestimonials(prev => prev.map(t => 
          t.id === testimonialId ? { ...t, isApproved } : t
        ));
      } else {
        setError(response.data.message || `Failed to ${isApproved ? 'approve' : 'reject'} testimonial`);
      }
    } catch (err) {
      setError(err.response?.data?.message || err.message || `An error occurred while ${isApproved ? 'approving' : 'rejecting'} testimonial`);
    }
  };

  const handleDeleteTestimonial = async (testimonialId) => {
    if (!window.confirm('Are you sure you want to delete this testimonial?')) {
      return;
    }

    try {
      const response = await api.delete(`/testimonials/${testimonialId}`);

      if (response.data.success) {
        // Remove the testimonial from the local state
        setTestimonials(prev => prev.filter(t => t.id !== testimonialId));
      } else {
        setError(response.data.message || 'Failed to delete testimonial');
      }
    } catch (err) {
      setError(err.response?.data?.message || err.message || 'An error occurred while deleting testimonial');
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  if (loading) {
    return (
      <div className="testimonials-container">
        <h2>Testimonials</h2>
        <div className="loading">Loading testimonials...</div>
      </div>
    );
  }

  return (
    <div className="testimonials-container">
      <h2>Testimonials</h2>
      
      {error && (
        <div className="error-message">
          {error}
          <button onClick={() => setError(null)} className="close-error-btn">×</button>
        </div>
      )}

      <div className="testimonials-table-container">
        <table className="testimonials-table">
          <thead>
            <tr>
              <th>ID</th>
              <th>User</th>
              <th>Content</th>
              <th>Rating</th>
              <th>Company</th>
              <th>Status</th>
              <th>Date</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {testimonials.length > 0 ? (
              testimonials.map((testimonial) => (
                <tr key={testimonial.id}>
                  <td>{testimonial.id}</td>
                  <td>
                    {testimonial.user ? testimonial.user.name : testimonial.fullName}
                    {testimonial.user && testimonial.company && (
                      <div className="user-company">{testimonial.company}</div>
                    )}
                  </td>
                  <td className="testimonial-content">{testimonial.content}</td>
                  <td>
                    <div className="rating">
                      {'★'.repeat(testimonial.rating)}
                      {'☆'.repeat(5 - testimonial.rating)}
                      <span>({testimonial.rating})</span>
                    </div>
                  </td>
                  <td>{testimonial.company || '-'}</td>
                  <td>
                    <span className={`status-badge ${testimonial.isApproved ? 'approved' : 'pending'}`}>
                      {testimonial.isApproved ? 'Approved' : 'Pending'}
                    </span>
                  </td>
                  <td>{formatDate(testimonial.created_at)}</td>
                  <td className="actions-cell">
                    {!testimonial.isApproved && (
                      <button
                        className="approve-btn"
                        onClick={() => handleApproveTestimonial(testimonial.id, true)}
                        title="Approve"
                      >
                        ✓
                      </button>
                    )}
                    {testimonial.isApproved && (
                      <button
                        className="reject-btn"
                        onClick={() => handleApproveTestimonial(testimonial.id, false)}
                        title="Reject"
                      >
                        ✗
                      </button>
                    )}
                    <button
                      className="delete-btn"
                      onClick={() => handleDeleteTestimonial(testimonial.id)}
                      title="Delete"
                    >
                      🗑️
                    </button>
                  </td>
                </tr>
              ))
            ) : (
              <tr>
                <td colSpan="8" className="no-data">No testimonials found</td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default Testimonials;